import type { PropsWithChildren } from 'react';
import { InteractionType } from '@azure/msal-browser';
import { MsalAuthenticationTemplate, MsalProvider } from '@azure/msal-react';

import { apiScopes, msalInstance } from './msalConfig';
import { isDemoModeRequested } from '@/lib/demoData';

/**
 * Wraps the app in an MSAL provider and gates rendering on a signed-in account.
 *
 * `MsalAuthenticationTemplate` triggers a redirect to Entra automatically when
 * no account is present, so calling `useHubUser()` deep in the tree can safely
 * assume `useAccount()` returns a valid account. The interaction type is
 * `Redirect` (not Popup) because popups get blocked by default in most
 * browsers and we have nothing to do that requires keeping the original tab
 * focused mid-sign-in.
 *
 * The requested scopes match `apiScopes` so the silent-token-acquire path in
 * `api/client.ts` can reuse the cached token without an extra round-trip on
 * the first API call.
 *
 * In demo mode (`VITE_DEMO_MODE=1`, or `?demo=1` in Vite dev builds only)
 * MSAL is bypassed entirely so the app renders without any Entra session —
 * useful for offline design reviews and PR screenshots.
 */
export function AuthProvider({ children }: PropsWithChildren) {
  // In demo mode MsalProvider is still rendered so useAccount()/useMsal() hooks
  // deeper in the tree have the required context. MsalAuthenticationTemplate is
  // skipped so no Entra redirect is triggered.
  //
  // isDemoModeRequested() is intentionally evaluated at render time here (above
  // BrowserRouter). The ?demo=1 query-param path only works in Vite dev mode,
  // where the URL is set before the app mounts and stays stable — client-side
  // navigation never changes the search params in practice. VITE_DEMO_MODE=1
  // (build-time) is always static. Neither path can silently flip mid-session.
  if (isDemoModeRequested()) {
    return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
  }
  return (
    <MsalProvider instance={msalInstance}>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={{ scopes: [...apiScopes] }}
      >
        {children}
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
}
